import {
    useWindowDimensions,
    TouchableOpacity,
    View
} from "react-native";
import type ActionRequiredSectionProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Sticker,
    Text
} from "ncore-ui-kit";
import {
    MessageSquareIcon,
    CalendarDaysIcon,
    ChevronRightIcon,
    AlertCircleIcon,
    Building2Icon,
    Trash2Icon,
    UsersIcon,
    TagIcon
} from "lucide-react-native";

const ActionRequiredSection = ({
    onNavigateToRequests,
    pendingParticipants,
    onNavigateToEvents,
    onNavigateToUsers,
    unverifiedUsers,
    requests
}: ActionRequiredSectionProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const hasRequests = Boolean(requests && (requests.total > 0 || (requests.contactForms && requests.contactForms > 0)));

    if(unverifiedUsers <= 0 && pendingParticipants <= 0 && !hasRequests) {
        return null;
    }

    const renderProfileRequestAlert = () => {
        if(!requests || requests.profileUpdates <= 0) {
            return null;
        }

        return <TouchableOpacity
            style={[
                stylesheet.alertCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    minWidth: isMobile ? "100%" : 320,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            onPress={() => onNavigateToRequests?.("profile")}
            activeOpacity={0.7}
        >
            <View
                style={[
                    stylesheet.alertContent,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.alertIconWrapper,
                        {
                            backgroundColor: colors.content.container.warning,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingSm
                        }
                    ]}
                >
                    <UsersIcon
                        color={colors.content.icon.warning}
                        size={20}
                    />
                </View>
                <View
                    style={stylesheet.alertTextWrapper}
                >
                    <Text
                        variant="titleSmallSize"
                    >
                        {localize("dashboard-action-pending-profile-requests", [
                            String(requests.profileUpdates)
                        ])}
                    </Text>
                    <Text
                        variant="labelSmallSize"
                        color="low"
                    >
                        {localize("dashboard-stat-profile-requests")}
                    </Text>
                </View>
            </View>
            <Sticker
                title={localize("dashboard-review-action")}
                type="warning"
                size="small"
            />
            <ChevronRightIcon
                color={colors.content.text.low}
                size={18}
            />
        </TouchableOpacity>;
    };

    const renderCompanyRequestAlert = () => {
        if(!requests || requests.companyUpdates <= 0) {
            return null;
        }

        return <TouchableOpacity
            style={[
                stylesheet.alertCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    minWidth: isMobile ? "100%" : 320,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            onPress={() => onNavigateToRequests?.("company")}
            activeOpacity={0.7}
        >
            <View
                style={[
                    stylesheet.alertContent,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.alertIconWrapper,
                        {
                            backgroundColor: colors.content.container.info,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingSm
                        }
                    ]}
                >
                    <Building2Icon
                        color={colors.content.icon.info}
                        size={20}
                    />
                </View>
                <View
                    style={stylesheet.alertTextWrapper}
                >
                    <Text
                        variant="titleSmallSize"
                    >
                        {localize("dashboard-action-pending-company-requests", [
                            String(requests.companyUpdates)
                        ])}
                    </Text>
                    <Text
                        variant="labelSmallSize"
                        color="low"
                    >
                        {localize("dashboard-stat-company-requests")}
                    </Text>
                </View>
            </View>
            <Sticker
                title={localize("dashboard-review-action")}
                size="small"
                type="info"
            />
            <ChevronRightIcon
                color={colors.content.text.low}
                size={18}
            />
        </TouchableOpacity>;
    };

    const renderCampaignRequestAlert = () => {
        if(!requests || requests.commercialOpportunities <= 0) {
            return null;
        }

        return <TouchableOpacity
            style={[
                stylesheet.alertCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    minWidth: isMobile ? "100%" : 320,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            onPress={() => onNavigateToRequests?.("campaign")}
            activeOpacity={0.7}
        >
            <View
                style={[
                    stylesheet.alertContent,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.alertIconWrapper,
                        {
                            backgroundColor: colors.content.container.info,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingSm
                        }
                    ]}
                >
                    <TagIcon
                        color={colors.content.icon.info}
                        size={20}
                    />
                </View>
                <View
                    style={stylesheet.alertTextWrapper}
                >
                    <Text
                        variant="titleSmallSize"
                    >
                        {localize("dashboard-action-pending-campaign-requests", [
                            String(requests.commercialOpportunities)
                        ])}
                    </Text>
                    <Text
                        variant="labelSmallSize"
                        color="low"
                    >
                        {localize("dashboard-stat-campaign-requests")}
                    </Text>
                </View>
            </View>
            <Sticker
                title={localize("dashboard-review-action")}
                size="small"
                type="info"
            />
            <ChevronRightIcon
                color={colors.content.text.low}
                size={18}
            />
        </TouchableOpacity>;
    };

    const renderDeletionRequestAlert = () => {
        if(!requests || requests.accountDeletions <= 0) {
            return null;
        }

        return <TouchableOpacity
            style={[
                stylesheet.alertCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    minWidth: isMobile ? "100%" : 320,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            onPress={() => onNavigateToRequests?.("deletion")}
            activeOpacity={0.7}
        >
            <View
                style={[
                    stylesheet.alertContent,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.alertIconWrapper,
                        {
                            backgroundColor: colors.content.container.danger,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingSm
                        }
                    ]}
                >
                    <Trash2Icon
                        color={colors.content.icon.danger}
                        size={20}
                    />
                </View>
                <View
                    style={stylesheet.alertTextWrapper}
                >
                    <Text
                        variant="titleSmallSize"
                    >
                        {localize("dashboard-action-pending-deletion-requests", [
                            String(requests.accountDeletions)
                        ])}
                    </Text>
                    <Text
                        variant="labelSmallSize"
                        color="low"
                    >
                        {localize("dashboard-stat-deletion-requests")}
                    </Text>
                </View>
            </View>
            <Sticker
                title={localize("dashboard-review-action")}
                type="danger"
                size="small"
            />
            <ChevronRightIcon
                color={colors.content.text.low}
                size={18}
            />
        </TouchableOpacity>;
    };

    const renderContactFormAlert = () => {
        if(!requests || !requests.contactForms || requests.contactForms <= 0) {
            return null;
        }

        return <TouchableOpacity
            style={[
                stylesheet.alertCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    minWidth: isMobile ? "100%" : 320,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            onPress={() => onNavigateToRequests?.("contact")}
            activeOpacity={0.7}
        >
            <View
                style={[
                    stylesheet.alertContent,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.alertIconWrapper,
                        {
                            backgroundColor: colors.content.container.warning,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingSm
                        }
                    ]}
                >
                    <MessageSquareIcon
                        color={colors.content.icon.warning}
                        size={20}
                    />
                </View>
                <View
                    style={stylesheet.alertTextWrapper}
                >
                    <Text
                        variant="titleSmallSize"
                    >
                        {localize("dashboard-action-pending-contact-forms", [
                            String(requests.contactForms)
                        ])}
                    </Text>
                    <Text
                        variant="labelSmallSize"
                        color="low"
                    >
                        {localize("dashboard-stat-contact-forms")}
                    </Text>
                </View>
            </View>
            <Sticker
                title={localize("dashboard-review-action")}
                type="warning"
                size="small"
            />
            <ChevronRightIcon
                color={colors.content.text.low}
                size={18}
            />
        </TouchableOpacity>;
    };

    const renderUnverifiedUsersAlert = () => {
        if(unverifiedUsers <= 0) {
            return null;
        }

        return <TouchableOpacity
            style={[
                stylesheet.alertCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    minWidth: isMobile ? "100%" : 320,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            onPress={onNavigateToUsers}
            activeOpacity={0.7}
        >
            <View
                style={[
                    stylesheet.alertContent,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.alertIconWrapper,
                        {
                            backgroundColor: colors.content.container.warning,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingSm
                        }
                    ]}
                >
                    <UsersIcon
                        color={colors.content.icon.warning}
                        size={20}
                    />
                </View>
                <View
                    style={stylesheet.alertTextWrapper}
                >
                    <Text
                        variant="titleSmallSize"
                    >
                        {localize("dashboard-action-unverified-users", [
                            String(unverifiedUsers)
                        ])}
                    </Text>
                    <Text
                        variant="labelSmallSize"
                        color="low"
                    >
                        {localize("dashboard-stat-pending-users")}
                    </Text>
                </View>
            </View>
            <Sticker
                title={localize("dashboard-review-action")}
                type="warning"
                size="small"
            />
            <ChevronRightIcon
                color={colors.content.text.low}
                size={18}
            />
        </TouchableOpacity>;
    };

    const renderPendingParticipantsAlert = () => {
        if(pendingParticipants <= 0) {
            return null;
        }

        return <TouchableOpacity
            style={[
                stylesheet.alertCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    minWidth: isMobile ? "100%" : 320,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            onPress={onNavigateToEvents}
            activeOpacity={0.7}
        >
            <View
                style={[
                    stylesheet.alertContent,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.alertIconWrapper,
                        {
                            backgroundColor: colors.content.container.info,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingSm
                        }
                    ]}
                >
                    <CalendarDaysIcon
                        color={colors.content.icon.info}
                        size={20}
                    />
                </View>
                <View
                    style={stylesheet.alertTextWrapper}
                >
                    <Text
                        variant="titleSmallSize"
                    >
                        {localize("dashboard-action-pending-participants", [
                            String(pendingParticipants)
                        ])}
                    </Text>
                    <Text
                        variant="labelSmallSize"
                        color="low"
                    >
                        {localize("dashboard-stat-event-participants")}
                    </Text>
                </View>
            </View>
            <Sticker
                title={localize("dashboard-review-action")}
                size="small"
                type="info"
            />
            <ChevronRightIcon
                color={colors.content.text.low}
                size={18}
            />
        </TouchableOpacity>;
    };

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingMd
            }
        ]}
    >
        <View
            style={[
                stylesheet.headerContainer,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <AlertCircleIcon
                color={colors.content.icon.warning}
                size={20}
            />
            <Text
                variant="titleMediumSize"
            >
                {localize("dashboard-action-required-title")}
            </Text>
        </View>
        <View
            style={[
                stylesheet.bannerGrid,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            {renderProfileRequestAlert()}
            {renderCompanyRequestAlert()}
            {renderCampaignRequestAlert()}
            {renderDeletionRequestAlert()}
            {renderContactFormAlert()}
            {renderUnverifiedUsersAlert()}
            {renderPendingParticipantsAlert()}
        </View>
    </View>;
};
export default ActionRequiredSection;
